/**
 * Synthora Hub Knowledge Base
 * 
 * This file contains all the knowledge the AI Assistant needs to understand
 * SynthoraHub and provide contextual help to users.
 */

// ============================================
// MODULE DEFINITIONS
// ============================================

export const MODULES = {
  'agent-directives': {
    name: 'Agent Directives',
    icon: '🤖',
    description: 'Configure how your AI agents behave by creating and managing directives.',
    purpose: 'This is where you define the personality, rules, and behavior of your three AI agents: Widget Agent, Observer Agent, and Summarizer Agent.',
    
    concepts: {
      directive: 'A directive is a set of instructions that tells the AI agent how to behave, what tone to use, and what rules to follow when interacting with users.',
      widgetAgent: 'The main conversational AI that users interact with. It follows your directive to provide advice in your area of expertise.',
      observerAgent: 'Monitors conversations in real-time and provides insights, suggestions, and alerts to the expert during live sessions.',
      summarizerAgent: 'Generates structured summaries of conversations after they end, extracting key points, action items, and follow-ups.',
    },
    
    actions: {
      generateDirective: {
        description: 'Create a completely new directive from your requirements. This will REPLACE your current directive.',
        howTo: 'Write your requirements in the text area (what you want the agent to do, how it should behave, any rules) and click "Generate New".',
        warning: 'This replaces your existing directive entirely. Use "Update" if you want to add to your existing directive.'
      },
      updateDirective: {
        description: 'Add new requirements to your existing directive without replacing it.',
        howTo: 'Write your additional requirements and click "Update". The AI will incorporate them into your existing directive.'
      },
      testAgent: {
        description: 'Try out your agent to see how it responds based on your directive.',
        howTo: 'Use the test panel on the right side. Type messages and see how your agent responds. This helps you refine your directive.'
      },
      switchAgentType: {
        description: 'Switch between Widget, Observer, and Summarizer agents.',
        howTo: 'Use the dropdown selector in the panel header to switch between agent types. Each has its own directive.'
      }
    },
    
    tips: [
      'Be specific in your requirements - the more detail you provide, the better the directive.',
      'Include examples of how you want the agent to respond.',
      'Mention things the agent should NEVER do.',
      'Test your directive with edge cases to make sure it handles unusual situations.',
      'Start simple and iterate - you can always update the directive later.'
    ]
  },
  
  'scenario-flows': {
    name: 'Scenario Flows',
    icon: '🔀',
    description: 'Build visual decision trees for complex scenarios your AI agent should handle.',
    purpose: 'Create flowcharts that define how the AI should handle specific situations, like "new client onboarding" or "document verification".',
    
    concepts: {
      flow: 'A visual representation of a scenario, showing the steps, decisions, and outcomes.',
      node: 'A single step in the flow. Different types include Start, Question, Condition, Document, AI Analysis, Action, and Outcome.',
      edge: 'A connection between two nodes, showing how the flow progresses.',
      startNode: 'Where every flow begins. Each flow has exactly one start node.',
      questionNode: 'A point where the AI asks the user something.',
      conditionNode: 'A decision point that branches into Yes/No paths based on a condition.',
      documentNode: 'Where the AI requests or analyzes documents.',
      aiAnalysisNode: 'Where the AI performs custom analysis based on a prompt.',
      actionNode: 'Where something happens (send email, create task, notify expert).',
      outcomeNode: 'An end point of the flow - success, failure, pending, or escalate.'
    },
    
    actions: {
      createFlow: {
        description: 'Create a new scenario flow from scratch.',
        howTo: 'Click "Create New Flow" button in the sidebar. A new flow with a Start node will be created.'
      },
      generateFromText: {
        description: 'Use AI to generate a flow from a text description.',
        howTo: 'Click "Generate from Text", describe your scenario in natural language, and the AI will create the flow structure for you.'
      },
      addNode: {
        description: 'Add a new node to your flow.',
        howTo: 'Use the toolbar at the top of the canvas. Click on the node type you want (Question, Condition, etc.) and it will appear in the center.'
      },
      connectNodes: {
        description: 'Connect nodes to define the flow path.',
        howTo: 'Drag from a node\'s handle (small circle) to another node\'s handle. For Condition nodes, use the left handle for "Yes" and right for "No".'
      },
      editNode: {
        description: 'Edit a node\'s properties.',
        howTo: 'Click on any node to select it. The properties panel on the right will show editable fields for that node type.'
      },
      saveFlow: {
        description: 'Save your current flow.',
        howTo: 'Click the "Save" button in the top-right corner. Your flow is saved to the database.'
      },
      convertToRules: {
        description: 'Convert your flow into a rule set.',
        howTo: 'Click "To Rules" button. The flow logic will be converted into structured rules that can be applied to your agent.'
      }
    },
    
    nodeTypes: {
      start: {
        icon: '🚀',
        color: '#10b981',
        description: 'The entry point of your flow. Every flow needs exactly one.',
        handles: { source: 1, target: 0 }
      },
      question: {
        icon: '❓',
        color: '#3b82f6',
        description: 'Ask the user a question. Configure the question text and expected answer type.',
        handles: { source: 1, target: 1 },
        properties: ['questionText', 'expectedAnswerType']
      },
      condition: {
        icon: '🔀',
        color: '#f59e0b',
        description: 'A decision point. Has two outputs: Yes (left) and No (right).',
        handles: { source: 2, target: 1 },
        properties: ['conditionType', 'conditionValue']
      },
      document: {
        icon: '📄',
        color: '#8b5cf6',
        description: 'Request or analyze documents from the user.',
        handles: { source: 1, target: 1 },
        properties: ['documentTypes', 'analysisPrompt']
      },
      'ai-analysis': {
        icon: '🤖',
        color: '#ec4899',
        description: 'Perform AI analysis with a custom prompt.',
        handles: { source: 1, target: 1 },
        properties: ['aiPrompt']
      },
      action: {
        icon: '⚡',
        color: '#14b8a6',
        description: 'Trigger an action like sending email or creating a task.',
        handles: { source: 1, target: 1 },
        properties: ['actionType']
      },
      outcome: {
        icon: '✅',
        color: '#6366f1',
        description: 'End point of a flow path. Can be success, failure, pending, or escalate.',
        handles: { source: 0, target: 1 },
        properties: ['outcomeType', 'outcomeMessage']
      }
    },
    
    tips: [
      'Start with simple flows and add complexity gradually.',
      'Use descriptive labels for nodes - it helps when reviewing the flow later.',
      'Condition nodes are powerful - use them to handle different user responses.',
      'Test your flow logic by tracing through different paths.',
      'You can generate flows from text descriptions to get started quickly.',
      'Flows can be converted to rule sets for more structured logic.'
    ]
  },
  
  'rule-sets': {
    name: 'Rule Sets Library',
    icon: '📋',
    description: 'Manage structured rules that define agent behavior.',
    purpose: 'View and manage rule sets that were generated from Scenario Flows. Rules provide explicit logic for how the AI should handle specific situations.',
    
    concepts: {
      ruleSet: 'A collection of related rules for a specific scenario or use case.',
      rule: 'A single conditional logic statement: IF conditions THEN actions.',
      condition: 'The "IF" part - what must be true for the rule to apply.',
      action: 'The "THEN" part - what happens when the rule matches.',
      priority: 'Higher priority rules are evaluated first. Use this to ensure important rules are checked first.',
      generatedDirective: 'A human-readable directive generated from the rules, which can be applied to an agent.'
    },
    
    actions: {
      selectRuleSet: {
        description: 'View the details of a rule set.',
        howTo: 'Click on any rule set card in the sidebar to see its rules and generated directive.'
      },
      generateDirective: {
        description: 'Create a readable directive from the rules.',
        howTo: 'Click "Generate" button in the directive panel. The AI will create a natural language directive from your structured rules.'
      },
      applyDirective: {
        description: 'Apply the generated directive to an agent.',
        howTo: 'Click "Apply" button, select which agent (Widget, Observer, or Summarizer), and confirm. The directive will be added to that agent.'
      },
      convertToFlow: {
        description: 'Convert rules back into a visual flow.',
        howTo: 'Click "To Flow" button. A new scenario flow will be created based on the rule logic.'
      },
      deleteRuleSet: {
        description: 'Delete a rule set.',
        howTo: 'Click the delete button and confirm. This cannot be undone.'
      }
    },
    
    tips: [
      'Rule sets are typically generated from Scenario Flows - that\'s the easiest way to create them.',
      'Generated directives can be reviewed and applied to any of your agents.',
      'Higher priority rules are checked first - use this for important exceptions.',
      'You can convert rule sets back to flows if you need to visualize or modify them.'
    ]
  },
  
  'knowledge-categories': {
    name: 'Knowledge Categories',
    icon: '📚',
    description: 'Organize your knowledge base for intelligent context retrieval.',
    purpose: 'Define categories that help the AI understand and classify user messages, so it can retrieve relevant information and respond appropriately.',
    
    concepts: {
      category: 'A topic area in your knowledge base (e.g., "Work Permits", "Tax Questions").',
      triggerKeywords: 'Words and phrases that indicate a message belongs to this category.',
      synonyms: 'Alternative ways to refer to this category.',
      priority: 'Higher priority categories take precedence when multiple categories match.',
      classification: 'The process of determining which category a user message belongs to.'
    },
    
    actions: {
      createCategory: {
        description: 'Add a new knowledge category.',
        howTo: 'Click "Add Category" button. Fill in the display name, description, trigger keywords, and select a color.'
      },
      editCategory: {
        description: 'Modify an existing category.',
        howTo: 'Click the edit (✏️) button on any category card.'
      },
      deleteCategory: {
        description: 'Remove a category.',
        howTo: 'Click the delete (🗑️) button on any category card and confirm.'
      },
      testClassification: {
        description: 'Test how a message would be classified.',
        howTo: 'Use the "Test Classification" panel at the bottom. Enter a message and click "Classify" to see which category it matches.'
      },
      resetToDefaults: {
        description: 'Reset all categories to system defaults.',
        howTo: 'Click "Reset to Defaults" button. This removes all custom categories.'
      }
    },
    
    tips: [
      'Use specific, relevant keywords for better classification accuracy.',
      'Test your categories with real user messages to see if they classify correctly.',
      'Higher priority categories win when multiple categories could match.',
      'Add synonyms to catch different ways users might express the same topic.'
    ]
  },
  
  'brain-learning': {
    name: 'Brain Learning',
    icon: '🧠',
    description: 'View AI-powered insights and improve your brain based on conversation performance.',
    purpose: 'Analyze how your AI is performing, identify areas for improvement, and apply suggestions to make your brain smarter over time.',
    
    concepts: {
      healthScore: 'A score from 0-100 that measures how well your brain is performing. Higher is better.',
      brainVersion: 'Each time you make significant changes, a new version is created to track performance over time.',
      suggestions: 'AI-generated recommendations for improving your brain based on conversation analysis.',
      conversationRating: 'Rate your conversations as excellent, good, needs improvement, or poor to help the AI learn.',
      successRate: 'The percentage of conversations rated positively (excellent or good).',
      perfectBrain: 'The complete brain system including Opportunity Maps, Teachings, and Training Mode.'
    },
    
    components: {
      opportunityMaps: {
        name: 'Opportunity Maps',
        description: 'Define all possible pathways with requirements, blockers, unlock factors, and alternatives.',
        purpose: 'Enable the AI to discover hidden opportunities and suggest alternative paths when the primary path fails.',
        missingMessage: 'You have no Opportunity Maps configured. This means the AI cannot discover alternative pathways for users.',
        howToCreate: 'Go to Opportunity Maps section and create maps for each of your knowledge categories. Define pathways with requirements and alternatives.',
        filtering: {
          description: 'Filter and search through your opportunity maps to find specific pathways',
          features: {
            textSearch: 'Search by pathway name, category, or description',
            statusFilter: 'Filter by active/inactive pathways',
            categoryFilter: 'Filter by knowledge category',
            sortOptions: 'Sort by name, date created, or usage count'
          },
          howToFilter: 'Use the filter controls at the top of the Opportunity Maps module to narrow down the list. Type in the search box to find specific pathways, or use dropdown filters for category and status.'
        }
      },
      teachings: {
        name: 'Teachings',
        description: 'Corrections and learnings captured from Training Mode sessions.',
        purpose: 'Allow the AI to learn from your expert corrections and apply them in future conversations.',
        missingMessage: 'You have no active Teachings. The AI has not learned any specific corrections from you yet.',
        howToCreate: 'Use Training Mode to simulate conversations, evaluate AI responses, and create teachings from your corrections.'
      },
      trainingSessions: {
        name: 'Training Sessions',
        description: 'Interactive sessions where you teach the AI by evaluating its responses.',
        purpose: 'Directly train your AI by simulating conversations and providing feedback.',
        missingMessage: 'You have not completed any Training Sessions. Training helps the AI learn your specific expertise.',
        howToCreate: 'Go to Training Mode, create a new session, simulate user conversations, rate AI responses, and provide corrections.'
      }
    },
    
    actions: {
      rateConversation: {
        description: 'Rate a conversation to help the AI learn.',
        howTo: 'In the "Conversations to Rate" section, click a rating button (excellent, good, needs improvement, poor) for each conversation.'
      },
      applySuggestion: {
        description: 'Apply an AI-generated improvement suggestion.',
        howTo: 'In the "Suggestions" section, click "Apply" on any suggestion to automatically implement it.'
      },
      refreshDashboard: {
        description: 'Get the latest brain health data.',
        howTo: 'Click the "Refresh" button in the header to reload all dashboard data.'
      }
    },
    
    tips: [
      'Rate conversations regularly to help the AI understand what works and what doesn\'t.',
      'Apply high-priority suggestions first - they have the biggest impact.',
      'Create Opportunity Maps for each of your knowledge categories.',
      'Use Training Mode to teach the AI about edge cases and specific scenarios.',
      'A higher health score means better AI performance. Aim for 80+.'
    ]
  },
  
  'training-mode': {
    name: 'Training Mode',
    icon: '🧪',
    description: 'Teach your AI through interactive training sessions.',
    purpose: 'Simulate conversations with your AI, evaluate its responses, and create "Teachings" that help it learn and improve.',
    
    concepts: {
      trainingSession: 'An interactive session where you role-play as a user and evaluate the AI\'s responses.',
      scenario: 'A specific situation or case type you want to train the AI on (e.g., "Work Permit Eligibility Check").',
      exchange: 'A single back-and-forth: your simulated user message and the AI\'s response.',
      evaluation: 'Your assessment of an AI response: rating (1-5 stars), correctness, completeness, and helpfulness.',
      teaching: 'A learnable unit created from your corrections. When you provide feedback, it becomes a Teaching that the AI applies in future conversations.',
      correction: 'What the AI should have said or done differently. This becomes part of a Teaching.'
    },
    
    actions: {
      startSession: {
        description: 'Begin a new training session.',
        howTo: 'Click "New Training Session" button. Give it a name and select the brain context (widget, observer, etc.).'
      },
      sendMessage: {
        description: 'Simulate a user message.',
        howTo: 'Type a message as if you were a user talking to your AI. Click send to see how the AI responds.'
      },
      evaluateResponse: {
        description: 'Rate and evaluate an AI response.',
        howTo: 'After the AI responds, rate it 1-5 stars, check if it was correct/complete/helpful, and optionally provide a correction.'
      },
      createTeaching: {
        description: 'Turn your correction into a Teaching.',
        howTo: 'Check "Create Teaching from this correction" when evaluating. The AI will learn from your feedback.'
      },
      completeSession: {
        description: 'Finish a training session.',
        howTo: 'Click "Complete Session" when done. The session will be saved and any Teachings will be ready for review.'
      },
      reviewTeachings: {
        description: 'Review and approve pending Teachings.',
        howTo: 'In the sidebar, view pending Teachings. Click "Apply" to activate them or "Reject" to discard.'
      }
    },
    
    tips: [
      'Simulate real user scenarios you\'ve encountered in your practice.',
      'Be specific in your corrections - tell the AI exactly what it should say.',
      'Low-rated responses (1-2 stars) automatically suggest creating Teachings.',
      'Complete multiple training sessions to build a comprehensive set of Teachings.',
      'Active Teachings are applied in all future conversations.'
    ]
  },
  
  'application-review': {
    name: 'Application Review',
    icon: '📋',
    description: 'Configure how the AI validates and reviews applications.',
    purpose: 'Set up directives for how the AI should check documents and applications for completeness and accuracy.',
    
    concepts: {
      applicationNote: 'Notes and directives specific to a type of application (e.g., "Work Permit Application").',
      validationRules: 'Rules that define what the AI should check in each application type.'
    },
    
    actions: {
      selectApplicationType: {
        description: 'Choose an application type to configure.',
        howTo: 'Use the dropdown to select from your defined application types.'
      },
      editNotes: {
        description: 'Update the review notes for an application type.',
        howTo: 'Edit the notes in the text area and click Save.'
      }
    },
    
    tips: [
      'Be thorough in your application review notes - include all required documents.',
      'Specify common mistakes the AI should watch for.',
      'Include instructions for what to do when something is missing.'
    ]
  },
  
  'expert-strategies': {
    name: 'Expert Strategies',
    icon: '🎯',
    description: 'Create creative workarounds and strategic solutions that capture expert knowledge.',
    purpose: 'Define the "what would a good lawyer/expert do" scenarios - the creative problem-solving approaches that differentiate a smart expert from basic AI.',
    
    concepts: {
      strategy: 'A playbook entry that captures expert wisdom for handling tricky situations. It defines WHEN to activate, WHAT to ask, WHY it works (the insight), and WHAT to do.',
      trigger: 'The condition that activates this strategy. Can be keywords in the user message, a blocked pathway, or a specific situation.',
      discoveryQuestion: 'A question to ask that might reveal hidden opportunities or change the situation.',
      expertInsight: 'THE KEY PART - the secret knowledge that a good expert knows but basic AI doesn\'t. This is what makes the strategy valuable.',
      action: 'What the AI should do when the strategy applies - suggest alternatives, request documents, pivot to a different pathway, etc.'
    },
    
    fields: {
      name: {
        description: 'A descriptive name for this strategy',
        example: 'Unskilled Job Title - Document Verification Strategy',
        tip: 'Use a name that clearly describes when this strategy is used'
      },
      description: {
        description: 'A longer explanation of what this strategy does and when to use it',
        example: 'When a user describes their job using an "unskilled" title, check the documents to see if we can legally use a better title.',
        tip: 'Write this as if explaining to a colleague'
      },
      triggers: {
        description: 'Conditions that activate this strategy. The system checks these against the current conversation.',
        types: {
          keyword: 'Words/phrases in the user message that trigger this strategy. Example: "not enough points", "don\'t qualify", "efficient", "helper"',
          userStatement: 'A specific phrase the user might say. Example: "I was working as..."',
          pathwayBlocked: 'Activates when a specific immigration/service pathway is blocked for the user',
          obstacle: 'Activates when a known obstacle is detected. Example: "crs_points_insufficient"',
          missingRequirement: 'Activates when a specific piece of user data is missing'
        },
        tip: 'Add multiple keywords that users might say when they need this strategy. Think of all the ways they might describe the situation.'
      },
      triggerLogic: {
        description: 'How to combine multiple triggers',
        options: {
          OR: 'Strategy activates if ANY trigger matches (most common)',
          AND: 'Strategy only activates if ALL triggers match (use for very specific situations)'
        },
        tip: 'Use OR for most strategies - you want it to activate when any of the keywords appear'
      },
      discoveryQuestions: {
        description: 'Questions to ask the user that might reveal hidden opportunities or change the outcome',
        fields: {
          question: 'The actual question to ask. Example: "Can you share your employment documents?"',
          purpose: 'Why ask this question - what are you hoping to discover? Example: "Verify the ACTUAL job title in documents - it may differ from what user said"'
        },
        tip: 'Think about what questions a smart expert would ask to find creative solutions. These are the questions that open new doors.'
      },
      expertInsight: {
        description: '⭐ THE MOST IMPORTANT FIELD ⭐ - The secret knowledge that makes this strategy valuable. What does a good expert know that others don\'t?',
        example: `"The user's VERBAL description of their job is NOT legally binding - only the DOCUMENTS matter. If documents say 'Kitchen Staff' not 'Efficient', and duties include cooking, we can legally write 'Cook' (NOC 6322 - Skilled Trade Level B). This transforms an ineligible case into an eligible one."`,
        tip: 'Write this as if you\'re teaching a junior colleague the "trick" or "secret" that took you years to learn. Be specific and explain WHY it works.'
      },
      actions: {
        description: 'What the AI should do when this strategy applies',
        types: {
          requestDocument: 'Ask for specific documents. Example: ROE, T4, employment letter',
          suggestAlternative: 'Recommend a different approach or pathway',
          pivotPathway: 'Switch to a completely different pathway',
          exploreOption: 'Investigate a new possibility',
          reframeSituation: 'Help the user see their situation differently'
        },
        fields: {
          action: 'Describe what to do. Example: "Request employment documents to verify actual job title"',
          userMessage: 'Optional - how to say this to the user. Example: "I\'d like to see your employment documents - your ROE, T4, or employment letter. The exact job title on these is very important."'
        },
        tip: 'Be specific about what the AI should do and optionally how to phrase it to the user'
      },
      category: {
        description: 'The knowledge category this strategy applies to (e.g., immigration, financial, startup)',
        tip: 'Strategies are filtered by category, so the AI only uses relevant strategies'
      },
      tags: {
        description: 'Keywords for organization and filtering',
        example: ['work-permit', 'noc-classification', 'document-strategy'],
        tip: 'Add tags that describe the scenario, pathway, or situation this strategy addresses'
      },
      priority: {
        description: 'Higher priority strategies are checked first (0-100)',
        tip: 'Use high priority (10+) for important strategies you want checked early'
      },
      status: {
        description: 'Whether the strategy is active',
        options: {
          draft: 'Not yet active - use while building',
          active: 'Live - will be used in conversations',
          archived: 'Disabled but preserved'
        }
      }
    },
    
    examples: {
      documentLoophole: {
        name: 'Unskilled Job Title - Document Verification',
        description: 'User says "I was efficient in a restaurant" (sounds unskilled), but we check documents and use the DOCUMENTED title instead',
        trigger: 'Keywords: efficient, helper, unskilled, assistant',
        question: '"Can you share your employment documents - ROE, T4?"',
        insight: 'The VERBAL description is not legally binding - only DOCUMENTS matter. If documents say "Kitchen Staff" and duties include cooking, we can write "Cook" (skilled job).',
        action: 'Request documents, check for discrepancy, use documented title'
      },
      pointsGap: {
        name: 'CRS Points Gap - Single Status Opportunity',
        description: 'User says "I don\'t have enough points" (seems like dead end), but being single is actually an OPPORTUNITY',
        trigger: 'Keywords: not enough points, can\'t qualify, below cutoff',
        question: '"Are you currently married or single?"',
        insight: 'Being SINGLE opens opportunities: spousal sponsorship route, more flexible for relocation, provincial programs. Never say "you don\'t qualify" - find alternatives!',
        action: 'Check marital status, suggest PNP, propose study pathway for points boost'
      }
    },
    
    actions: {
      createStrategy: {
        description: 'Create a new expert strategy',
        howTo: 'Click "New Strategy" button. Fill in the fields starting with Name and Description, then add Triggers, Discovery Questions, the Expert Insight, and Actions.'
      },
      addTrigger: {
        description: 'Add a trigger condition',
        howTo: 'In the Triggers section, click "Add Trigger". Select the type and enter the keywords or conditions.'
      },
      addDiscoveryQuestion: {
        description: 'Add a question to discover opportunities',
        howTo: 'In the Discovery Questions section, click "Add Discovery Question". Enter the question and its purpose.'
      },
      writeExpertInsight: {
        description: 'Write the key expert knowledge',
        howTo: 'In the Expert Insight section, write the "secret" that makes this strategy valuable. Be specific - explain what, why, and how.'
      },
      addAction: {
        description: 'Add an action for the AI to take',
        howTo: 'In the Actions section, click "Add Action". Select the type, describe the action, and optionally provide wording for the user.'
      },
      testStrategy: {
        description: 'See if your strategy triggers correctly',
        howTo: 'Save the strategy as "active", then test in a widget conversation using the trigger keywords.'
      }
    },
    
    tips: [
      'Start with the Expert Insight - this is the most valuable part. What do you know that others don\'t?',
      'Think about real cases where you found a creative solution. Those are perfect strategies.',
      'Use multiple trigger keywords - think of all the ways a user might describe the situation.',
      'Discovery questions should open doors - ask things that might reveal new opportunities.',
      'Be specific in actions - tell the AI exactly what to do and say.',
      'Strategies work alongside Opportunity Maps - use strategies for the creative "tricks", use Maps for pathway eligibility.',
      'A good strategy turns a "no" into a "maybe" or "yes, but differently".',
      'Less is more - a focused strategy with one good insight is better than a complex one with many weak ideas.'
    ]
  },
  
  'opportunity-maps': {
    name: 'Opportunity Maps',
    icon: '🗺️',
    description: 'Define pathways and alternatives for intelligent opportunity discovery.',
    purpose: 'Create maps that help the AI find the best pathway for each user and suggest alternatives when the primary path fails.',
    
    concepts: {
      opportunityMap: 'A collection of related pathways for a specific category (e.g., "Work Permit Pathways", "Study Permit Pathways").',
      pathway: 'A specific route to achieve a goal (e.g., "LMIA Work Permit", "Open Work Permit", "PGWP").',
      requirements: 'Conditions that must be true for the user to qualify for this pathway (e.g., "has valid job offer", "employer willing to get LMIA").',
      blockers: 'Conditions that prevent eligibility (e.g., "is inadmissible", "has criminal record").',
      unlockFactors: 'Questions to ask that might reveal hidden opportunities (e.g., "Are you married to a Canadian?", "Do you have a degree?").',
      alternatives: 'Other pathways to suggest when this one fails (e.g., if Work Permit fails, suggest Study Permit).',
      primaryPathway: 'The main recommended pathway when multiple options exist.',
      fallbackPathway: 'A backup option when the primary pathway is not available.'
    },
    
    structure: {
      description: 'An Opportunity Map has this structure:',
      example: `{
  "categoryName": "Work Permit Pathways",
  "pathways": [
    {
      "name": "LMIA Work Permit",
      "requirements": ["has job offer", "employer can get LMIA"],
      "blockers": ["is inadmissible"],
      "unlockFactors": ["Are you currently in Canada?", "Is your employer experienced with LMIA?"],
      "alternatives": ["Open Work Permit", "Study Permit"]
    },
    {
      "name": "Open Work Permit",
      "requirements": ["spouse is on valid work/study permit"],
      "blockers": ["is inadmissible"],
      "alternatives": ["LMIA Work Permit"]
    }
  ]
}`
    },
    
    actions: {
      createMap: {
        description: 'Create a new Opportunity Map.',
        howTo: 'Go to the Opportunity Maps section and click "Create New Map". Select a knowledge category to associate it with.'
      },
      addPathway: {
        description: 'Add a pathway to your map.',
        howTo: 'In your map, click "Add Pathway". Enter the pathway name and configure requirements, blockers, unlock factors, and alternatives.'
      },
      defineRequirements: {
        description: 'Set what conditions must be true for this pathway.',
        howTo: 'In the pathway editor, add requirements like "has job offer = true" or "years of experience >= 2".'
      },
      defineBlockers: {
        description: 'Set what conditions prevent eligibility.',
        howTo: 'Add blockers like "is inadmissible = true" or "has criminal record = true".'
      },
      addUnlockFactors: {
        description: 'Add questions that might reveal new opportunities.',
        howTo: 'These are questions the AI should ask to discover hidden possibilities. Example: "Are you married to a Canadian citizen?"'
      },
      setAlternatives: {
        description: 'Define fallback pathways.',
        howTo: 'Select other pathways that the AI should suggest if this pathway fails.'
      }
    },
    
    tips: [
      'Create one Opportunity Map per knowledge category for best organization.',
      'Think about ALL ways a user could achieve their goal, not just the obvious path.',
      'Unlock factors are powerful - they help the AI discover opportunities users might not know about.',
      'Always set alternatives so users never hit a dead end.',
      'Use specific requirements like "years of experience >= 2" instead of vague ones.',
      'Blockers should be absolute deal-breakers, not just complications.',
      'Review real conversations to identify common alternative paths you suggest.'
    ],
    
    examples: {
      immigration: `**Immigration Opportunity Map Example:**
- Category: Work Permits
- Pathways:
  1. LMIA Work Permit (requires job offer + employer LMIA)
  2. Open Work Permit (requires spouse on permit)
  3. Post-Graduation Work Permit (requires Canadian degree)
  4. IEC Working Holiday (requires eligible nationality + age)
- Each pathway has alternatives pointing to others`,
      
      financial: `**Financial Services Opportunity Map Example:**
- Category: Mortgage Products
- Pathways:
  1. Prime Mortgage (requires good credit + stable income)
  2. Alternative Mortgage (requires 20%+ down payment)
  3. Private Mortgage (last resort option)
- Unlock factors: "Are you self-employed?", "Do you have a co-signer?"`
    }
  }
};

// ============================================
// GENERAL SYNTHORA KNOWLEDGE
// ============================================

export const GENERAL_KNOWLEDGE = {
  whatIsSynthora: `Synthora Hub is your personalization center for configuring AI agents. It allows you to:
- Define how your AI agents behave through directives
- Build visual decision flows for complex scenarios  
- Manage structured rules for agent logic
- Organize your knowledge base for intelligent responses
- Train your AI through interactive Training Mode
- Create Opportunity Maps for intelligent pathway discovery
- Build a "Perfect Brain" that learns and improves over time`,

  workflow: `The typical workflow in Synthora Hub:
1. Start with **Agent Directives** to set up basic agent behavior
2. Create **Scenario Flows** for complex decision trees
3. Convert flows to **Rule Sets** for structured logic
4. Organize **Knowledge Categories** for smart context retrieval
5. **Create Opportunity Maps** to define pathways and alternatives
6. Use **Training Mode** to teach the AI edge cases
7. Monitor **Brain Learning** to track performance and apply improvements`,

  agents: {
    widget: 'Your main conversational AI that users interact with directly. It provides advice and assistance based on your directives.',
    observer: 'Runs in the background during live sessions, providing real-time insights and suggestions to you (the expert).',
    summarizer: 'Automatically generates summaries after conversations end, extracting key information and action items.'
  },
  
  perfectBrain: {
    whatIs: `The "Perfect Brain" is an advanced AI system that combines all your knowledge into a smart, learning system. It includes:
- **Directives** - How your AI behaves
- **Scenario Flows** - Visual decision trees
- **Rule Sets** - Structured logic
- **Knowledge Categories** - Topic classification
- **Opportunity Maps** - Pathway discovery with alternatives
- **Expert Strategies** - Creative workarounds ("what a good expert would do")
- **Teachings** - Corrections the AI has learned
- **Training Sessions** - Interactive teaching sessions`,
    
    expertStrategies: `**Expert Strategies** capture the creative problem-solving knowledge that makes a great expert.

**Each strategy defines:**
- **Triggers** - When to activate (keywords, situations)
- **Discovery Questions** - What to ask to find opportunities
- **Expert Insight** - The SECRET KNOWLEDGE (most important!)
- **Actions** - What the AI should do

**Example:**
When a user says "I don't have enough points", a basic AI says "sorry, not eligible."
An expert with a Strategy asks: "Are you married?" - because being SINGLE is actually an OPPORTUNITY for spousal sponsorship routes!

**Strategies vs. Opportunity Maps:**
- Maps define WHAT pathways exist and eligibility
- Strategies define HOW to creatively solve problems

Use Strategies for the "tricks of the trade" that took you years to learn.`,
    
    opportunityMaps: `**Opportunity Maps** define all possible pathways for your users. Each pathway has:
- **Requirements** - What the user needs to qualify
- **Blockers** - What prevents eligibility
- **Unlock Factors** - Questions that reveal new possibilities
- **Alternatives** - Fallback options when the primary path fails

For example, if someone doesn't qualify for a Work Permit, the AI can suggest a Study Permit or Provincial Nominee program instead.`,
    
    teachings: `**Teachings** are learnings captured from your corrections. When you:
1. Simulate a conversation in Training Mode
2. Rate the AI's response poorly
3. Provide a correction

That correction becomes a "Teaching" that the AI applies in all future similar situations.`,
    
    healthScore: `Your **Brain Health Score** (0-100) measures overall brain performance:
- **80-100**: Excellent - Your brain is performing great
- **60-79**: Good - Some areas could be improved
- **40-59**: Needs Attention - Several components are missing or underperforming
- **0-39**: Critical - Major components are missing

The score is based on: conversation success rate, coverage, Opportunity Maps, Teachings, and Training sessions.`
  }
};

// ============================================
// HELP RESPONSES
// ============================================

export const COMMON_QUESTIONS = {
  'how do I start': `Great question! Here's how to get started with Synthora Hub:

1. **Start with Agent Directives** - Define how your Widget Agent should behave
2. Write your requirements (what you want the agent to do, how it should talk, any rules)
3. Click "Generate New" to create your first directive
4. Create **Scenario Flows** for complex decision trees
5. Set up **Knowledge Categories** for topic classification
6. Create **Opportunity Maps** to define pathways (important for the Perfect Brain!)
7. Use **Training Mode** to teach the AI edge cases

Would you like me to explain any of these steps in more detail?`,

  'what is a directive': `A **directive** is a set of instructions that tells your AI agent how to behave.

Think of it like training a new employee - you're telling the AI:
- What its job is
- How it should communicate (formal, friendly, etc.)
- What topics it should focus on
- What it should never do
- How to handle specific situations

You can update your directive at any time to refine the agent's behavior.`,

  'how do flows work': `**Scenario Flows** are visual decision trees that define how your AI handles specific situations.

Imagine a flowchart:
- It starts with a **Start** node
- **Question** nodes ask the user for information
- **Condition** nodes make decisions (Yes/No branches)
- **Document** nodes request or analyze files
- **Action** nodes trigger things (emails, tasks)
- **Outcome** nodes end the flow (success, failure, etc.)

You can create flows manually using the toolbar, or describe your scenario in text and let AI generate it for you!`,

  'difference between generate and update': `Great question! There's an important difference:

**Generate New** - Creates a completely new directive from scratch. Your old directive is REPLACED entirely.

**Update** - Adds your new requirements to your existing directive. The AI incorporates them without losing what you already have.

Use **Update** when you want to add something new.
Use **Generate New** when you want to start fresh or completely change direction.`,

  'what is an opportunity map': `An **Opportunity Map** is a key component of the Perfect Brain that defines all possible pathways for your users.

Each Opportunity Map contains **Pathways**, and each pathway has:
- **Requirements** - What conditions must be met (e.g., "has job offer = true")
- **Blockers** - What prevents eligibility (e.g., "is inadmissible = true")
- **Unlock Factors** - Questions that might reveal new possibilities (e.g., "Are you married?")
- **Alternatives** - Other pathways to suggest if this one fails

**Example for Immigration:**
If someone doesn't qualify for a Work Permit, the AI can suggest:
- Study Permit pathway
- Provincial Nominee program
- Spousal sponsorship (if married)

This is how the AI finds "hidden opportunities" instead of just saying "you don't qualify."`,

  'how do I create an opportunity map': `To create an **Opportunity Map**:

1. Go to the Opportunity Maps section (coming soon in UI, currently via API)
2. Create a map for each of your knowledge categories (e.g., "Work Permit Pathways")
3. Add pathways with:
   - **Name** - e.g., "LMIA Work Permit"
   - **Requirements** - What the user needs
   - **Unlock Factors** - Questions to ask
   - **Alternatives** - Fallback options

**Tip:** Think about all the ways a user could achieve their goal, not just the obvious path.`,

  'what is training mode': `**Training Mode** lets you teach your AI through interactive sessions.

Here's how it works:
1. Start a new training session
2. Type messages as if you were a user
3. See how the AI responds
4. Rate the response (1-5 stars)
5. Mark if it was correct, complete, and helpful
6. Provide a correction if needed

When you provide a correction, it becomes a **Teaching** - a learnable unit that the AI applies in future conversations.

It's like having a practice session with a new employee!`,

  'what is a teaching': `A **Teaching** is a correction that the AI has learned from you.

When you:
1. Use Training Mode to simulate a conversation
2. Rate an AI response poorly
3. Provide what the AI *should* have said

That correction becomes a Teaching. The AI will apply this teaching in all future similar situations.

**Example:**
- AI said: "You need 1 year of experience"
- You corrected: "For this specific program, only 6 months is required"
- Teaching created: The AI now knows the correct requirement`,

  'how do I improve my brain score': `Your **Brain Health Score** can be improved by:

1. **Create Opportunity Maps** - Define pathways for each category
2. **Complete Training Sessions** - Teach the AI edge cases
3. **Rate Conversations** - Help the AI learn what works
4. **Apply Suggestions** - Implement AI-recommended improvements
5. **Add Flows & Rules** - Cover more scenarios

**Components that affect your score:**
- Conversation success rate (50%)
- Opportunity Maps quality (15%)
- Active Teachings (15%)
- Training session performance (20%)

Aim for 80+ for excellent performance!`,

  'what is the perfect brain': `The **Perfect Brain** is the complete AI personalization system that combines:

**Classic Components:**
- Agent Directives (how AI behaves)
- Scenario Flows (visual decision trees)
- Rule Sets (structured logic)
- Knowledge Categories (topic classification)

**Advanced Components:**
- Opportunity Maps (pathway discovery with alternatives)
- Expert Strategies (creative workarounds - "what a good lawyer would do")
- Teachings (corrections the AI has learned)
- Training Sessions (interactive teaching)
- Smart Context (sends only relevant data to AI)

When all components are configured, your AI can:
✅ Classify user intent
✅ Find the best pathway
✅ Discover hidden opportunities
✅ Apply creative expert strategies
✅ Suggest alternatives when paths fail
✅ Apply learnings from your corrections
✅ Continuously improve over time`,

  'what is an expert strategy': `An **Expert Strategy** is a playbook entry that captures your expert wisdom for handling tricky situations.

**It answers:** "What would a GOOD expert do when they encounter this situation?"

**Structure:**
- **Triggers** - When should this strategy activate? (keywords, situations)
- **Discovery Questions** - What to ask to find hidden opportunities
- **Expert Insight** - THE SECRET KNOWLEDGE (the most important part!)
- **Actions** - What the AI should do

**Example - "Unskilled Job Title" Strategy:**
- **Trigger:** User says "I was efficient" or "helper" (sounds unskilled)
- **Question:** "Can you share your employment documents?"
- **Insight:** The user's VERBAL description is NOT legally binding - only DOCUMENTS matter. If documents say "Kitchen Staff" and duties include cooking, we can write "Cook" (skilled job)!
- **Action:** Request documents, verify actual title, use documented title

This transforms a case that looks ineligible into an eligible one!`,

  'how do I create an expert strategy': `To create an **Expert Strategy**:

1. Go to **Synthora Hub → Expert Strategies**
2. Click **"New Strategy"**
3. Fill in the fields:

**Basic Info:**
- **Name** - Descriptive name (e.g., "Points Gap - Marriage Opportunity")
- **Description** - Explain when/why to use this
- **Category** - Which area (immigration, financial, etc.)

**Triggers (when to activate):**
- Add keywords users might say: "not enough points", "can't qualify"
- Think of ALL ways users might describe the situation

**Discovery Questions:**
- Questions that reveal opportunities
- Example: "Are you currently married?"

**Expert Insight (MOST IMPORTANT!):**
- Write the SECRET KNOWLEDGE - what you know that others don't
- Be specific - explain WHY it works

**Actions:**
- What should the AI do?
- How should it phrase things to the user?

5. Set status to **"Active"** when ready
6. Test it in a conversation!`,

  'what should I write in expert insight': `The **Expert Insight** is THE MOST IMPORTANT field in an Expert Strategy.

**It should capture:**
- What a GOOD expert knows that a basic AI doesn't
- The "trick" or "secret" that took you years to learn
- WHY this approach works

**Example - Job Title Strategy:**
❌ **Bad insight:** "Check the documents"
✅ **Good insight:** "The user's VERBAL description is NOT legally binding - only DOCUMENTS matter. If documents say 'Kitchen Staff' not 'Efficient', and duties include cooking, we can legally write 'Cook' (NOC 6322 - Skilled Trade Level B). This transforms an ineligible case into an eligible one!"

**Example - Points Gap Strategy:**
❌ **Bad insight:** "Ask if they're married"
✅ **Good insight:** "Being SINGLE is actually an OPPORTUNITY - they can explore spousal sponsorship routes, international marriage options, or provincial programs that favor single applicants. Never say 'you don't qualify' - find alternatives!"

**Tips:**
- Write as if teaching a junior colleague
- Be specific with codes, requirements, legal basis
- Explain the LOGIC behind the strategy`
};



// ============================================
// CONTEXTUAL HELP GENERATOR
// ============================================

/**
 * Generate contextual help based on the current screen state
 */
export const getContextualHelp = (context) => {
  const { currentModule, selectedElements, formState, uiState } = context;
  const moduleInfo = MODULES[currentModule];
  
  if (!moduleInfo) {
    return {
      greeting: "Hello! I'm your Synthora assistant. How can I help you today?",
      suggestions: [
        "What is Synthora Hub?",
        "How do I get started?",
        "Explain the different modules"
      ]
    };
  }
  
  // Generate context-specific suggestions
  const suggestions = [];
  
  if (currentModule === 'scenario-flows') {
    if (!selectedElements.flow) {
      suggestions.push("How do I create a new flow?");
      suggestions.push("What is the 'Generate from Text' feature?");
    } else if (selectedElements.node) {
      const nodeType = selectedElements.node.type;
      suggestions.push(`How do I configure a ${nodeType} node?`);
      suggestions.push("How do I connect this to other nodes?");
    } else {
      suggestions.push("How do I add nodes to my flow?");
      suggestions.push("What are the different node types?");
    }
  }
  
  if (currentModule === 'agent-directives') {
    if (!formState.requirements || formState.requirements.trim() === '') {
      suggestions.push("What should I write in my requirements?");
      suggestions.push("Show me an example directive");
    } else {
      suggestions.push("Should I use Generate or Update?");
      suggestions.push("How do I test my directive?");
    }
  }
  
  if (currentModule === 'rule-sets') {
    if (!selectedElements.ruleSet) {
      suggestions.push("Where do rule sets come from?");
      suggestions.push("How do I create a rule set?");
    } else {
      suggestions.push("How do I apply this to my agent?");
      suggestions.push("Can I convert this to a flow?");
    }
  }
  
  if (currentModule === 'knowledge-categories') {
    suggestions.push("What are trigger keywords?");
    suggestions.push("How does classification work?");
    suggestions.push("How do I test my categories?");
  }
  
  if (currentModule === 'brain-learning') {
    suggestions.push("How do I improve my brain score?");
    suggestions.push("What is an Opportunity Map?");
    suggestions.push("What components am I missing?");
    suggestions.push("What is the Perfect Brain?");
  }
  
  if (currentModule === 'training-mode') {
    suggestions.push("How does Training Mode work?");
    suggestions.push("What is a Teaching?");
    suggestions.push("How do I create a Teaching?");
    suggestions.push("How do I evaluate AI responses?");
  }
  
  if (currentModule === 'application-review') {
    suggestions.push("How do I configure application review?");
    suggestions.push("What should I include in review notes?");
  }
  
  if (currentModule === 'expert-strategies') {
    suggestions.push("What is an Expert Strategy?");
    suggestions.push("How do I create an Expert Strategy?");
    suggestions.push("What should I write in Expert Insight?");
    suggestions.push("What are good trigger keywords?");
  }
  
  return {
    greeting: `I see you're in **${moduleInfo.name}**. ${moduleInfo.description} How can I help?`,
    suggestions: suggestions.slice(0, 3)
  };
};

export default {
  MODULES,
  GENERAL_KNOWLEDGE,
  COMMON_QUESTIONS,
  getContextualHelp
};


